import { useMemo, useRef, useState } from 'react';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { Section } from '~/components/section';

import { Careers, Department, Location } from '../models/careers';

import { DepartmentList } from './department-list';
import { JobList } from './job-list';
import { SelectLocation } from './select-location';

export function CareersSection({ careers, title }: { careers: Careers; title: string }) {
  const [selectedLocation, setSelectedLocation] = useState<Location | 'all'>('all');
  const [selectedDepartment, setSelectedDepartment] = useState<Department>(careers.departments[0]);
  const openPositionsRef = useRef<HTMLDivElement>(null);

  const filteredJobs = useMemo(() => {
    return careers.jobs.filter((job) => {
      return (
        job.departmentName === selectedDepartment &&
        (selectedLocation === 'all' || job.locationName === selectedLocation)
      );
    });
  }, [careers.jobs, selectedDepartment, selectedLocation]);

  function handleLocationSelect(location: Location) {
    setSelectedLocation(location);
  }

  function handleDepartmentSelect(department: Department) {
    openPositionsRef.current?.scrollIntoView({ behavior: 'instant' });
    setSelectedDepartment(department);
  }

  return (
    <Section backgroundColor='gray.50' id='open-positions'>
      <Flex ref={openPositionsRef} flexDirection='column' textAlign='center'>
        <Heading as='h1' size='marketing-4xl'>
          {title}
        </Heading>
      </Flex>

      <Flex flexDirection={{ base: 'column', lg: 'row' }} gap={{ base: '24px', lg: '56px' }} marginTop='48px'>
        {/* Sidebar */}
        <Box width={{ base: '100%', lg: '280px' }}>
          <Flex flexDirection='column' gap='8px' position='sticky' top='96px'>
            <SelectLocation locations={careers.locations} onLocationSelect={handleLocationSelect} />

            <DepartmentList
              departments={careers.departments}
              selectedDepartment={selectedDepartment}
              onDepartmentSelect={handleDepartmentSelect}
            />
          </Flex>
        </Box>

        {/* Main Content */}
        <Box flex='1'>
          <Box>
            <Heading as='h2' size='marketing-2xl'>
              {selectedDepartment}
            </Heading>
            <Text fontSize='12px'>
              {filteredJobs.length} Open Position{filteredJobs.length !== 1 && 's'}
            </Text>
          </Box>

          {selectedLocation && <JobList jobs={filteredJobs} />}
        </Box>
      </Flex>
    </Section>
  );
}
